import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Paper,
  Typography,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const PlanHeader = ({ 
  header, 
  index, 
  onDelete, 
  onChange, 
  onAddSubheader,
  onMoveUp,
  onMoveDown,
  onMoveToHeader,
  onMoveToSubheader,
  canMoveUp,
  canMoveDown,
  canMoveToHeader,
  canMoveToSubheader,
  isSubheader,
  initialEditing = false
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');

  // Initialize edit value when header changes
  useEffect(() => {
    if (header) {
      setEditValue(header.content || '');
    }
  }, [header]);

  // Handle initialEditing changes
  useEffect(() => {
    if (initialEditing) {
      setIsEditing(true);
      setEditValue(header.content || '');
    }
  }, [initialEditing, header]);

  if (!header) return null;

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = () => {
    if (editValue.trim()) {
      onChange(editValue);
    }
    setIsEditing(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    }
  };

  return (
    <Paper
      elevation={1}
      sx={{
        p: 1,
        mb: 1,
        display: 'flex',
        alignItems: 'center',
        ml: isSubheader ? 4 : 0,
        position: 'relative',
        borderLeft: isSubheader ? '2px solid' : 'none',
        borderLeftColor: 'primary.main',
        '&:hover': {
          backgroundColor: 'action.hover'
        }
      }}
    >
      {isEditing ? (
        <TextField
          fullWidth
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyPress={handleKeyPress}
          autoFocus
          size="small"
          sx={{ mr: 1 }}
          placeholder={isSubheader ? "Enter subsection title..." : "Enter section title..."}
        />
      ) : (
        <Typography 
          onClick={handleEdit}
          variant={isSubheader ? 'body1' : 'h6'}
          sx={{ 
            flex: 1,
            cursor: 'pointer',
            '&:hover': { color: 'primary.main' },
            fontWeight: isSubheader ? 'normal' : 'medium',
            mr: 1
          }}
        >
          {header.content}
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {canMoveUp && (
          <Tooltip title="Move Up">
            <IconButton size="small" onClick={onMoveUp}>
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        
        {canMoveDown && (
          <Tooltip title="Move Down">
            <IconButton size="small" onClick={onMoveDown}>
              <ArrowDownwardIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {isSubheader && canMoveToHeader && (
          <Tooltip title="Move to Header Level">
            <IconButton size="small" onClick={onMoveToHeader} color="primary">
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {!isSubheader && canMoveToSubheader && (
          <Tooltip title="Move to Subheader Level">
            <IconButton size="small" onClick={onMoveToSubheader} color="primary">
              <ArrowForwardIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        
        <Tooltip title="Delete">
          <IconButton size="small" onClick={onDelete} color="error">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Paper>
  );
};

export default PlanHeader; 